import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getMeter as fetchMeter,
  postMeter,
  getAnonymousMeter as fetchAnonymousMeter,
  postAnonymousMeter,
  patchMeteringSurfCheck,
} from '../api/meter';
import { ANONYMOUS_ID_CONFLICT_CODE } from '../common/constants';
import { computeLimitedTimeOfferEligibility } from '../common/metering';
import { generateAnonymousId } from '../utils/identify';

export const setMeter = createAction('SET_METER');

export const performMeteringSurfCheck = createAsyncThunk(
  'PERFORM_METER_SURF_CHECK',
  /**
   * @param {object} args
   * @param {string} args.accessToken
   */
  async ({ accessToken }) => {
    if (!accessToken) {
      throw new Error('Access token required for this operation.');
    }
    const { meterRemaining } = await patchMeteringSurfCheck(accessToken);
    return { meterRemaining };
  },
);

export const createMeter = createAsyncThunk(
  'CREATE_METER',
  /**
   * @async
   * @param {object} args
   * @param {string} args.accessToken
   * @param {string} args.anonymousId
   * @param {string} args.timezone
   * @param {string} args.countryCode
   * @param {boolean?} args.performMeterSurfCheck
   */
  async (
    { accessToken, anonymousId, countryCode, timezone, performMeterSurfCheck = false },
    thunkApi,
  ) => {
    try {
      let meter = await postMeter({
        accessToken,
        anonymousId: anonymousId || generateAnonymousId(),
        timezone,
        countryCode,
      });
      if (performMeterSurfCheck) {
        const {
          payload: { meterRemaining },
        } = await thunkApi.dispatch(performMeteringSurfCheck({ accessToken }));
        meter = {
          ...meter,
          meterRemaining,
        };
      }
      return meter;
    } catch (error) {
      const res = await error.response?.json();
      if (res?.code === ANONYMOUS_ID_CONFLICT_CODE) {
        let meter = await postMeter({
          anonymousId: generateAnonymousId(),
          accessToken,
          countryCode,
          timezone,
        });
        if (performMeterSurfCheck) {
          const {
            payload: { meterRemaining },
          } = await thunkApi.dispatch(performMeteringSurfCheck({ accessToken }));
          meter = {
            ...meter,
            meterRemaining,
          };
        }
        return meter;
      }
      throw error;
    }
  },
);

export const getMeter = createAsyncThunk(
  'GET_METER',
  /**
   * @param {object} args
   * @param {string} args.accessToken
   * @param {boolean?} args.createIfNotFound
   * @param {string} args.anonymousId
   * @param {string} args.timezone
   * @param {string} args.countryCode
   * @param {boolean?} args.performMeterSurfCheck
   */
  async (
    {
      accessToken,
      createIfNotFound = false,
      anonymousId = null,
      countryCode = null,
      timezone = null,
      performMeterSurfCheck = false,
    } = {},
    thunkApi,
  ) => {
    if (!accessToken) {
      throw new Error('Access token required to get meter.');
    }

    try {
      let meter = await fetchMeter(accessToken);
      const limitedTimeOffer = computeLimitedTimeOfferEligibility(accessToken);
      const { limitedTimeOfferEligible, limitedTimeOfferDaysRemaining, limitedTimeOfferLink } =
        limitedTimeOffer || {};
      if (performMeterSurfCheck) {
        const {
          payload: { meterRemaining },
        } = await thunkApi.dispatch(performMeteringSurfCheck({ accessToken }));
        meter = {
          ...meter,
          meterRemaining,
        };
      }

      return {
        ...meter,
        limitedTimeOfferEligible,
        limitedTimeOfferDaysRemaining,
        limitedTimeOfferLink,
      };
    } catch (err) {
      if (createIfNotFound && err.statusCode === 404) {
        await thunkApi.dispatch(
          createMeter({ accessToken, countryCode, anonymousId, timezone, performMeterSurfCheck }),
        );
        return { createdNewMeter: true };
      }
      throw err;
    }
  },
);

export const getAnonymousMeter = createAsyncThunk(
  'GET_ANONYMOUS_METER',
  /**
   * @param {object} args
   * @param {string} args.anonymousId
   * @param {string} args.rateLimitTreatment
   * @param {'on' | 'off'} args.treatmentState
   * @param {string} args.treatmentVariant
   */
  async ({ anonymousId, rateLimitTreatment, treatmentState, treatmentVariant }) => {
    // Check if this anonymous session has used any surf checks before
    const meter = await fetchAnonymousMeter(anonymousId);
    // If there is no key stored (new anonymous session) then meterRemaining would be null sent from the API
    const { meterRemaining } = meter;
    const hasMeter = meterRemaining !== null;
    // If it's a new anonymous session then save the anonymousId in Redis with meterRemaining as 1
    if (!hasMeter) {
      await postAnonymousMeter(anonymousId);
    }

    return {
      anonymousId,
      treatmentName: rateLimitTreatment,
      treatmentState,
      treatmentVariant,
      meterLimit: 0,
      meterRemaining: hasMeter ? meterRemaining : 1,
    };
  },
);
